import React, { useMemo } from 'react'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import { useIntl, FormattedMessage } from 'react-intl'
import KidsFlyFreeTermsAndConditions from 'components/Common/KidsFlyFreeTermsAndConditions'
import { shouldShowKidsFlyFreeLabel } from 'lib/charterFlights/kidsFlyFreeUtils'

interface Props {
  finePrint: string
  offer: App.Offer
}

function FinePrint(props: Props) {
  const { finePrint, offer } = props
  const intl = useIntl()

  const cancellationPolicies = useMemo(() => offer.packages.reduce<Utils.PartialRecord<App.CancellationPolicyType, Array<string>>>((acc, pkg) => {
    const cancellationPolicy = pkg.roomRate?.cancellationPolicy

    if (cancellationPolicy?.type) {
      acc[cancellationPolicy?.type] = cancellationPolicy.policyDetail
    }

    return acc
  }, {}), [offer.packages])

  return (
    <Group direction="vertical" gap={20}>
      {!!offer.property?.taxesAndFeesContent &&
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'ul64eBDVbL6AwfnpdKyt',
              defaultMessage: 'Taxes & fees:',
              description: 'Form field label for taxes and fees section in the fine print details section',
            })}
          </BodyText>
          <MarkdownRender type="compact" fontSize="medium" content={offer.property?.taxesAndFeesContent} />
        </Group>}

      {offer.type === OFFER_TYPE_ALWAYS_ON && Object.entries(cancellationPolicies).filter(([_, policy]) => !!policy).map(([policyType, policy], index) => (
        <Group direction="vertical" gap={12} key={policyType}>
          {index === 0 && <BodyText variant="medium" weight="bold">{intl.formatMessage({
            id: 'knBEfB0uUHC7bbL8MKNA',
            defaultMessage: 'Cancellation Policy',
            description: 'Heading for the cancellation policy section in the cancellation flow',
          })}
          </BodyText>}
          {policy.map((item, index) => (
            <BodyText variant="medium" key={index}>{item}</BodyText>
          ))}
        </Group>
      ))}

      {('dynamic' in cancellationPolicies) &&
        <BodyText variant="medium">
          <FormattedMessage
            id="_vobIb6L6CxVdvzRPpk8"
            defaultMessage="Please note cancellation policies vary depending on the rate selected, please choose your package carefully. See full <link>booking terms and conditions</link>"
            description="Note about varying cancellation policies with link to booking terms and conditions"
            values={{
              link: (chunks) => <TextLink weight="regular" to="/booking-policies#lux-premium-collection" target="_blank">{chunks}</TextLink>,
            }}
          />
        </BodyText>}
      <MarkdownRender type="compact" fontSize="medium" content={finePrint} />
      {shouldShowKidsFlyFreeLabel(offer) && <KidsFlyFreeTermsAndConditions />}
    </Group>
  )
}

export default FinePrint
